import React, { useContext, useEffect, useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import SummaryApi from '../common'
import Context from '../context'
import displayINRCurrency from '../helpers/displayCurrency'
import { MdDelete, MdVerified, MdClose } from "react-icons/md";
import { FaPlus, FaMinus, FaShoppingCart, FaChevronRight } from "react-icons/fa"
import service from '../common/HttpService'

import { useSelector } from 'react-redux'
import { getCookie } from '../common/cookieHelper'
import useTranslate from '../hooks/useTranslate'

const CartDrawer = ({ onClose }) => {
    const context = useContext(Context)
    const { cartItems, fetchUserAddToCart } = context
    const user = useSelector(state => state?.user?.user)
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [cartSummary, setCartSummary] = useState(null)
    const [translatedItems, setTranslatedItems] = useState([])

    const t = useTranslate({
        myBag: "My Bag",
        item: "Item",
        items: "Items",
        emptyBag: "Your bag is empty",
        emptyBagDesc: "ADD SOMETHING TO START FRESH",
        shopProducts: "Shop Products",
        bagSubtotal: "Bag Subtotal",
        discountApplied: "Discount Applied",
        shippingCharge: "Shipping Charge",
        free: "FREE",
        toPay: "To Pay",
        proceed: "Proceed",
        extraSavings: "Extra savings of",
        applied: "applied!"
    });

    const { translateText, languageCode } = context

    const fetchCartData = async () => {
        setLoading(true)
        try {
            const userId = user?._id || user?.id || user?.user_id || getCookie('user_id') || localStorage.getItem('user_id');
            const response = await service.get(SummaryApi.view_cart.url)
            
            let newCart = [];
            let newSummary = {
                cart: [],
                cart_total: 0,
                total_mrp: 0,
                discount: 0,
                shipping_charge: 0
            };

            if (response && (response.success === "true" || response.success === true)) {
                const extra = response.extraData;
                if (extra && typeof extra === 'object') {
                    if (Array.isArray(extra.cart)) {
                        newCart = extra.cart;
                        newSummary = { ...extra, cart: extra.cart };
                    } else if (Array.isArray(extra)) {
                        newCart = extra;
                        newSummary = { ...newSummary, cart: extra };
                    } else {
                        newCart = [];
                        newSummary = { ...extra, cart: [] };
                    }
                } else if (Array.isArray(response.data)) {
                    newCart = response.data;
                    newSummary = { ...newSummary, cart: response.data };
                }
            } else {
                // If response.success is false or empty, the cart is empty
                newCart = [];
                newSummary = {
                    cart: [],
                    cart_total: 0,
                    total_mrp: 0,
                    discount: 0,
                    shipping_charge: 0
                };
            }

            setCartSummary(newSummary)
            setTranslatedItems(newCart)

            if (typeof fetchUserAddToCart === 'function') {
                await fetchUserAddToCart()
            }
        } catch (error) {
            console.error("Error fetching cart summary:", error)
            setCartSummary({
                cart: [],
                cart_total: 0,
                total_mrp: 0,
                discount: 0,
                shipping_charge: 0
            })
            setTranslatedItems([])
            if (typeof fetchUserAddToCart === 'function') {
                await fetchUserAddToCart()
            }
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchCartData()
    }, [user])

    useEffect(() => {
        window.__activeModals = (window.__activeModals || 0) + 1;
        document.body.style.overflow = 'hidden';
        return () => {
            window.__activeModals = (window.__activeModals || 0) - 1;
            if (window.__activeModals <= 0) {
                document.body.style.overflow = 'unset';
                window.__activeModals = 0;
            }
        };
    }, []);

    const updateQty = async (id, qty, change) => {
        const userId = user?._id || user?.id || user?.user_id || getCookie('user_id') || localStorage.getItem('user_id');
        const newQty = Number(qty) + change
        if (newQty <= 0) {
            await deleteCartProduct(id)
            return
        }

        // Optimistically update quantity in local cartSummary and translatedItems
        setCartSummary(prev => {
            if (!prev) return prev;
            const currentList = Array.isArray(prev.cart) ? prev.cart : (Array.isArray(prev) ? prev : []);
            const updated = currentList.map(item => {
                const itemId = (item.cart_id && !isNaN(Number(item.cart_id))) ? item.cart_id : ((item.id && !isNaN(Number(item.id))) ? item.id : (item.id || item.cart_id || item._id));
                return String(itemId) === String(id) ? { ...item, qty: newQty, quantity: newQty } : item;
            });
            return Array.isArray(prev) ? updated : { ...prev, cart: updated };
        });

        setTranslatedItems(prev => prev.map(item => {
            const itemId = (item.cart_id && !isNaN(Number(item.cart_id))) ? item.cart_id : ((item.id && !isNaN(Number(item.id))) ? item.id : (item.id || item.cart_id || item._id));
            return String(itemId) === String(id) ? { ...item, qty: newQty, quantity: newQty } : item;
        }));

        try {
            await service.post(SummaryApi.update_cart.url, {
                id: id,
                qty: newQty,
                user_id: userId
            })
        } catch (err) {
            console.error("Error updating cart:", err);
        } finally {
            await fetchCartData()
        }
    }

    const deleteCartProduct = async (id) => {
        const userId = user?._id || user?.id || user?.user_id || getCookie('user_id') || localStorage.getItem('user_id');

        // Optimistically remove item from cartSummary and translatedItems immediately
        setCartSummary(prev => {
            if (!prev) return { cart: [], cart_total: 0, total_mrp: 0, discount: 0, shipping_charge: 0 };
            const currentList = Array.isArray(prev.cart) ? prev.cart : (Array.isArray(prev) ? prev : []);
            const updated = currentList.filter(item => {
                const itemId = (item.cart_id && !isNaN(Number(item.cart_id))) ? item.cart_id : ((item.id && !isNaN(Number(item.id))) ? item.id : (item.id || item.cart_id || item._id));
                return String(itemId) !== String(id);
            });
            return Array.isArray(prev) ? updated : { ...prev, cart: updated };
        });

        setTranslatedItems(prev => prev.filter(item => {
            const itemId = (item.cart_id && !isNaN(Number(item.cart_id))) ? item.cart_id : ((item.id && !isNaN(Number(item.id))) ? item.id : (item.id || item.cart_id || item._id));
            return String(itemId) !== String(id);
        }));

        // Also update context cart items immediately if setCartItems is available
        if (typeof context.setCartItems === 'function') {
            context.setCartItems(prev => {
                if (!Array.isArray(prev)) return [];
                return prev.filter(item => {
                    const itemId = (item.cart_id && !isNaN(Number(item.cart_id))) ? item.cart_id : ((item.id && !isNaN(Number(item.id))) ? item.id : (item.id || item.cart_id || item._id));
                    return String(itemId) !== String(id);
                });
            });
        }
        if (typeof context.setCartProductCount === 'function') {
            context.setCartProductCount(prev => Math.max(0, (Number(prev) || 1) - 1));
        }

        try {
            await service.post(SummaryApi.delete_cart.url, {
                id: id,
                user_id: userId
            })
        } catch (err) {
            console.error("Error deleting cart product:", err);
        } finally {
            await fetchCartData()
        }
    }

    const parsePrice = (price) => {
        if (!price || price === "0") return 0;
        if (typeof price === 'number') return price;
        if (typeof price === 'string') {
            // Remove commas and handle formatted currency strings
            const cleaned = price.replace(/,/g, '').trim();
            return Number(cleaned) || 0;
        }
        return 0;
    };

    // Normalize display items:
    // If cartSummary is loaded, use its cart array.
    // If cartSummary is not loaded yet (null), fall back to cartItems from context.
    const displayData = useMemo(() => {
        if (cartSummary !== null) {
            if (Array.isArray(cartSummary?.cart)) {
                return cartSummary.cart;
            }
            if (Array.isArray(cartSummary)) {
                return cartSummary;
            }
            return [];
        }
        return Array.isArray(cartItems) ? cartItems : [];
    }, [cartSummary, cartItems]);

    const getProductPrice = (item) => {
        const rate = (item?.item_rate && item?.item_rate?.[0]) || (item?.item_rates && item?.item_rates?.[0]) || {};
        const p = item?.productId || item?.product || {};
        const price = rate.mrp || item.mrp || p.rates?.[0]?.mrp || p.price || p.mrp || 0;
        return parsePrice(price);
    };

    const getProductSellingPrice = (item) => {
        const rate = (item?.item_rate && item?.item_rate?.[0]) || (item?.item_rates && item?.item_rates?.[0]) || {};
        const p = item?.productId || item?.product || {};
        const price = rate.salerate || item.salerate || p.salerate || p.rates?.[0]?.salerate || p.sellingPrice || 0;
        return parsePrice(price);
    };

    const getProductShippingCharge = (item) => {
        const rate = (item?.item_rate && item?.item_rate?.[0]) || (item?.item_rates && item?.item_rates?.[0]) || {};
        const p = item?.productId || item?.product || {};
        const charge = item?.shipping_charges || item?.shipping_charge || rate?.shipping_charges || rate?.shipping_charge || p?.shipping_charges || p?.shipping_charge || p?.rates?.[0]?.shipping_charges || 0;
        return parsePrice(charge);
    };

    const totalQty = displayData.reduce((prev, curr) => prev + (Number(curr.qty || curr.quantity) || 1), 0)
    const itemsSubtotal = displayData.reduce((prev, curr) => prev + ((Number(curr.qty || curr.quantity) || 1) * getProductSellingPrice(curr)), 0)
    const originalPrice = displayData.reduce((prev, curr) => prev + ((Number(curr.qty || curr.quantity) || 1) * getProductPrice(curr)), 0)
    const totalShipping = displayData.length === 0 ? 0 : (
        displayData.reduce((max, curr) => Math.max(max, getProductShippingCharge(curr)), 0) ||
        (cartSummary?.shipping_charge ? parsePrice(cartSummary.shipping_charge) : 0)
    )
    const totalPrice = cartSummary?.cart_total ? parsePrice(cartSummary.cart_total) : (itemsSubtotal + totalShipping)
    const savings = originalPrice > itemsSubtotal ? originalPrice - itemsSubtotal : 0

    useEffect(() => {
        const translateCartItems = async () => {
            if (!displayData.length || !languageCode || languageCode === 'en') {
                setTranslatedItems(displayData);
                return;
            }

            try {
                const translated = await Promise.all(displayData.map(async (item) => {
                    const rate = (item.item_rate && item.item_rate.length > 0) ? item.item_rate[0] : (item.item_rates && item.item_rates.length > 0 ? item.item_rates[0] : {});
                    const p = item.productId || item.product || {};
                    const pName = item.itemname || rate.itemname || p.productName || "Product";
                    const pBrand = rate.brand_name || item.brand_name || p.brandName || p.brand || 'FasalShop';
                    const unitName = rate.unit_name || item.unit || p.unit || 'Unit';

                    return {
                        ...item,
                        translatedName: await translateText(pName, languageCode),
                        translatedBrand: await translateText(pBrand, languageCode),
                        translatedUnit: await translateText(unitName, languageCode)
                    };
                }));
                setTranslatedItems(translated);
            } catch (error) {
                console.error("Cart translation error:", error);
                setTranslatedItems(displayData);
            }
        };
        translateCartItems();
    }, [displayData, languageCode, translateText]);

    return (
        <div className='fixed inset-0 z-[100] flex justify-end overflow-hidden transition-all'>
            {/* Backdrop */}
            <div
                className='absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-in fade-in transition-opacity cursor-pointer'
                onClick={onClose}
            ></div>

            {/* Drawer Content */}
            <div className='relative w-full max-w-[450px] md:max-w-md bg-white h-full shadow-2xl flex flex-col transform transition-transform duration-500 animate-in slide-in-from-right-full'>

                {/* DRAWER HEADER */}
                <div className='shrink-0 bg-gradient-to-r from-[#143d2c] to-[#0d2a1f] p-6 text-white relative overflow-hidden'>
                    <div className='absolute -top-10 -left-10 w-32 h-32 bg-white/5 rounded-full blur-3xl'></div>
                    <div className='absolute -bottom-10 -right-10 w-32 h-32 bg-[#00b259]/20 rounded-full blur-3xl'></div>

                    <div className='relative z-10 flex items-center justify-between'>
                        <div className='flex items-center gap-4'>
                            <div className='w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-[#00ff9d] border border-white/10'>
                                <FaShoppingCart size={22} />
                            </div>
                            <div>
                                <h2 className='text-lg font-black uppercase tracking-[2px] text-white leading-tight'>{t.myBag}</h2>
                                <p className='text-[#00ff9d] text-[10px] font-bold uppercase tracking-[3px] mt-1'>
                                    {totalQty} {totalQty === 1 ? t.item : t.items}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className='w-10 h-10 bg-white/10 hover:bg-white/20 rounded-xl flex items-center justify-center transition-all'
                        >
                            <MdClose size={24} />
                        </button>
                    </div>
                </div>

                {/* SCROLLABLE ITEMS LIST */}
                <div className='flex-1 overflow-y-auto p-4 custom-scrollbar bg-[#f8faf9]'>
                    {displayData.length === 0 ? (
                        <div className='h-full flex flex-col items-center justify-center text-center p-8'>
                            <div className='w-32 h-32 bg-gray-50 rounded-full flex items-center justify-center mb-6'>
                                <FaShoppingCart size={40} className='text-gray-200' />
                            </div>
                            <h2 className='text-lg font-black text-[#143d2c] uppercase'>{t.emptyBag}</h2>
                            <p className='text-gray-400 text-[10px] mt-2 uppercase tracking-widest font-bold'>{t.emptyBagDesc}</p>
                            <button
                                onClick={onClose}
                                className='mt-8 bg-[#143d2c] text-white px-8 py-3 rounded-xl font-black text-[10px] uppercase tracking-[3px] shadow-lg'
                            >
                                {t.shopProducts}
                            </button>
                        </div>
                    ) : (
                        <div className='space-y-4 pt-2'>
                            {(translatedItems.length === displayData.length ? translatedItems : displayData).map((item, index) => {
                                const rate = (item.item_rate && item.item_rate.length > 0) ? item.item_rate[0] : (item.item_rates && item.item_rates.length > 0 ? item.item_rates[0] : {});
                                const p = item.productId || item.product || {};
                                const backendBaseUrl = (SummaryApi.allProduct.url || "").split('/Api')[0]

                                const pName = item.translatedName || item.itemname || rate.itemname || p.productName || "Product";
                                const pBrand = item.translatedBrand || rate.brand_name || item.brand_name || p.brandName || p.brand || 'FasalShop';
                                const unitName = item.translatedUnit || rate.unit_name || item.unit || p.unit || 'Unit';

                                let pImg = item.image || p.productImage?.[0] || p.image || "";
                                if (pImg === "https://www.fasalshop.com/product-images/") {
                                    pImg = rate.image || p.image || "";
                                }

                                if (typeof pImg === 'string' && pImg && !pImg.startsWith('http')) {
                                    const cleanPath = pImg.startsWith('/') ? pImg.substring(1) : pImg;
                                    pImg = `${backendBaseUrl}/${cleanPath}`;
                                } else if (!pImg) {
                                    pImg = "https://placehold.co/400x400/eaeff6/2a4b7c?text=FS";
                                }

                                const pPrice = getProductPrice(item);
                                const pSellingPrice = getProductSellingPrice(item);
                                const currentQty = Number(item.qty || item.quantity) || 1;
                                const itemId = (item.cart_id && !isNaN(Number(item.cart_id))) ? item.cart_id : ((item.id && !isNaN(Number(item.id))) ? item.id : (item.id || item.cart_id || item._id));

                                return (
                                    <div key={itemId + "-" + index} className='bg-white border border-slate-100 rounded-[1.25rem] p-4 flex gap-5 hover:shadow-xl hover:shadow-green-900/5 transition-all group'>
                                        {/* IMAGE CONTAINER */}
                                        <div className='w-24 h-24 bg-[#f8fcf9] rounded-2xl p-3 shrink-0 flex items-center justify-center border border-[#eef5ef] overflow-hidden'>
                                            <img
                                                src={pImg}
                                                className='max-w-full max-h-full object-contain mix-blend-multiply group-hover:scale-110 transition-transform duration-500'
                                                alt={pName}
                                            />
                                        </div>

                                        {/* INFO CONTAINER */}
                                        <div className='flex-1 min-w-0 flex flex-col justify-between py-1'>
                                            <div>
                                                <div className='flex justify-between items-start gap-2'>
                                                    <h4 className='text-xs font-black text-[#143d2c] uppercase truncate tracking-tight pr-1 leading-tight'>{pName}</h4>
                                                    <button onClick={() => deleteCartProduct(itemId)} className='text-slate-200 hover:text-red-500 transition-colors bg-slate-50 p-1.5 rounded-lg hover:bg-red-50'>
                                                        <MdDelete size={14} />
                                                    </button>
                                                </div>
                                                <div className='flex items-center gap-2 mt-1.5'>
                                                    <p className='text-[#00b259] text-[8px] font-black uppercase tracking-widest'>{pBrand}</p>
                                                    <span className='text-slate-200 text-[8px]'>•</span>
                                                    <p className='text-slate-400 text-[8px] font-bold uppercase tracking-widest'>{unitName}</p>
                                                </div>
                                            </div>

                                            <div className='flex items-center justify-between mt-auto'>
                                                <div className='flex flex-col'>
                                                    <span className='text-[14px] font-black text-[#143d2c] leading-tight'>{displayINRCurrency(pSellingPrice)}</span>
                                                    {pPrice > pSellingPrice && (
                                                        <span className='text-[9px] text-slate-300 line-through font-bold'>{displayINRCurrency(pPrice)}</span>
                                                    )}
                                                </div>

                                                <div className='flex items-center bg-[#f8fcf9] rounded-xl border border-[#eef5ef] p-1 shadow-sm w-[90px]'>
                                                    <button
                                                        onClick={() => updateQty(itemId, currentQty, -1)}
                                                        className='w-7 h-7 flex items-center justify-center text-[#143d2c] bg-white hover:bg-red-50 hover:text-red-500 rounded-lg transition-all shadow-sm'
                                                    >
                                                        <FaMinus size={8} />
                                                    </button>
                                                    <span className='flex-1 text-center text-[11px] font-black text-[#143d2c]'>{currentQty}</span>
                                                    <button
                                                        onClick={() => updateQty(itemId, currentQty, 1)}
                                                        className='w-7 h-7 flex items-center justify-center text-[#143d2c] bg-white hover:bg-[#00b259] hover:text-white rounded-lg transition-all shadow-sm'
                                                    >
                                                        <FaPlus size={8} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>

                {/* STICKY FOOTER SUMMARY */}
                {displayData.length > 0 && (
                    <div className='shrink-0 bg-white p-6 border-t border-gray-100 shadow-[0_-15px_40px_rgba(0,0,0,0.05)]'>
                        <div className='space-y-3 mb-5'>
                            <div className='flex justify-between items-center text-[9px] uppercase font-black tracking-widest text-gray-400'>
                                <span>{t.bagSubtotal}</span>
                                <span>{displayINRCurrency(originalPrice)}</span>
                            </div>
                            {savings > 0 && (
                                <div className='flex justify-between items-center text-[9px] uppercase font-black tracking-widest text-[#00b259]'>
                                    <span>{t.discountApplied}</span>
                                    <span>- {displayINRCurrency(savings)}</span>
                                </div>
                            )}
                            <div className='flex justify-between items-center text-[9px] uppercase font-black tracking-widest text-gray-400'>
                                <span>{t.shippingCharge}</span>
                                <span className={totalShipping === 0 ? 'text-[#00b259]' : 'text-[#143d2c] font-black'}>
                                    {totalShipping === 0 ? t.free : displayINRCurrency(totalShipping)}
                                </span>
                            </div>

                            <div className='pt-4 border-t border-gray-50 flex justify-between items-center'>
                                <div className='flex flex-col'>
                                    <span className='text-[9px] font-black text-gray-400 uppercase tracking-widest'>{t.toPay}</span>
                                    <span className='text-xl font-black text-[#143d2c] tracking-tighter'>{displayINRCurrency(totalPrice)}</span>
                                </div>
                                <button
                                    onClick={() => {
                                        onClose();
                                        context.setCheckoutData(null);
                                        context.setShowCheckout(true);
                                    }}
                                    className='bg-[#00b259] hover:bg-[#00c15f] text-white h-12 px-8 rounded-xl font-black text-[10px] uppercase tracking-[3px] shadow-lg shadow-green-900/10 flex items-center gap-2 active:scale-95 transition-all'
                                >
                                    {t.proceed}
                                    <FaChevronRight size={8} />
                                </button>
                            </div>
                        </div>

                        {savings > 0 && (
                            <div className='bg-[#00b259]/5 border border-[#00b259]/10 rounded-lg py-2 px-3 flex items-center gap-2'>
                                <MdVerified className='text-[#00b259]' size={12} />
                                <span className='text-[8px] font-black text-[#00b259] uppercase tracking-widest'>
                                    {t.extraSavings} {displayINRCurrency(savings)} {t.applied}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
 .custom-scrollbar::-webkit-scrollbar { width: 4px; }
 .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0,178,89,0.15); border-radius: 10px; }
 .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
 ` }} />
        </div>
    )
}

export default CartDrawer
